import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import EnterprisePayment from '../components/EnterprisePayment';
import { Building, Users, Calendar, Zap, Shield, MessageSquare, AlertTriangle } from 'lucide-react';
import Modal from '../components/Modal';
import ContactForm from '../components/ContactForm';

const CreateEnterprise = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [isRenewal, setIsRenewal] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [enterpriseId, setEnterpriseId] = useState('');
  const [initializationError, setInitializationError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isRenewalParam = params.get('renewal') === 'true';
    const renewalState = location.state?.isRenewal;
    const orgName = location.state?.organizationName || '';
    const eId = location.state?.enterpriseId || '';

    if (isRenewalParam || renewalState) {
      setIsRenewal(true);
      setOrganizationName(orgName);
      setEnterpriseId(eId);

      // If this is a renewal, we MUST have an enterpriseId.
      // If not, it's a critical error in the navigation state.
      if (!eId) {
        setInitializationError("Could not identify the enterprise to renew. Please go back to your account page and try again.");
      }
    }
  }, [location]);

  const handleCancel = () => {
    navigate('/account');
  };

  const handleContactSuccess = () => {
    setContactSuccess(true);
    setTimeout(() => {
      setContactModalOpen(false);
      setContactSuccess(false);
    }, 3000);
  };

  const benefits = [
    {
      icon: Users,
      title: 'Centralized Management',
      text: isRenewal ? 'Continue managing access for your entire team from one place' : 'Easily manage access for your entire team from one place',
      renewalOnly: false,
    },
    {
      icon: Calendar,
      title: 'Simplified Billing',
      text: 'One payment for your entire team at $540 per seat',
      renewalOnly: false,
    },
    {
      icon: Zap,
      title: isRenewal ? 'Seamless Transition' : 'Easy Onboarding',
      text: isRenewal ? 'No need to re-invite team members - they keep their existing access' : 'Team members join with a simple enterprise key',
      renewalOnly: false,
    },
    {
      icon: Shield,
      title: 'Access Control',
      text: isRenewal ? 'Keep managing user access as your team changes' : 'Add or remove users as your team changes',
      renewalOnly: false,
    },
    {
      icon: Building,
      title: 'Admin Dashboard',
      text: isRenewal ? 'Continue using your admin dashboard to manage enterprise users' : 'View and manage all enterprise users from your dashboard',
      renewalOnly: false,
    }
  ];

  if (isRenewal) {
    benefits.unshift({
      icon: Shield,
      title: 'Restore Team Access',
      text: 'All team members will regain access immediately after renewal',
      renewalOnly: true
    });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto p-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">
            {isRenewal ? 'Renew Enterprise Account' : 'Create Enterprise Account'}
          </h1>
          <p className="text-gray-600">
            {isRenewal
              ? 'Renew your enterprise account to restore access for your team members'
              : 'Set up an enterprise account to manage your team\'s access to Dessino.ai'
            }
          </p>
        </div>

        {initializationError ? (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
            <div className="flex">
              <div className="py-1"><AlertTriangle className="h-6 w-6 text-red-500 mr-4" /></div>
              <div>
                <p className="font-bold">Initialization Error</p>
                <p>{initializationError}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <EnterprisePayment
                onCancel={handleCancel}
                isRenewal={isRenewal}
                organizationName={organizationName}
                enterpriseId={enterpriseId}
              />
            </div>

            <div className="space-y-6">
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-4">
                  {isRenewal ? 'Renewal Benefits' : 'Enterprise Benefits'}
                </h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div className="flex" key={index}>
                      <div className="flex-shrink-0">
                        <benefit.icon className={`h-6 w-6 ${benefit.renewalOnly ? 'text-green-500' : 'text-blue-500'}`} />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-md font-medium">{benefit.title}</h3>
                        <p className="text-sm text-gray-500">{benefit.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-900 mb-2">{isRenewal ? 'Need to Change Your Plan?' : 'Need More Seats?'}</h3>
                <p className="text-sm text-blue-800 mb-4">{isRenewal ? 'For plan changes or custom enterprise options, please contact our sales team.' : 'For larger teams or custom enterprise plans, please contact our sales team.'}</p>
                <button onClick={() => setContactModalOpen(true)} className="inline-flex items-center text-sm text-blue-700 hover:text-blue-900 font-medium"><MessageSquare className="w-4 h-4 mr-1" />Contact Enterprise Sales →</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)} title="Contact Enterprise Sales">
        {contactSuccess ? (
          <div className="bg-green-50 border border-green-200 text-green-800 p-6 rounded-lg">
            <h3 className="font-bold text-lg mb-2">Message Sent!</h3>
            <p>Thank you for contacting us. Our enterprise team will get back to you as soon as possible.</p>
          </div>
        ) : (
          <ContactForm onSuccess={handleContactSuccess} />
        )}
      </Modal>
    </div>
  );
};

export default CreateEnterprise;